import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import RuleEditor from './RuleEditor';
import { DEFAULT_METRICS } from './TenantRulesSection';

/**
 * Agency-level overrides section (`agency_objective_overrides`).
 *
 * Visible only when tenant.permission_matrix.allow_agency_target_override === true.
 * The parent (`/admin/objective-rules` index) handles that gate.
 *
 * - agency_owner / agency_admin: scoped to their own agency_id (passed via props).
 * - tenant_admin / super_admin: can pick any agency under the tenant via dropdown.
 */

const formatRuleSummary = (ruleType, ruleValue, t) => {
  if (!ruleType) return '—';
  switch (ruleType) {
    case 'flat':
      return `Flat: ${ruleValue?.value ?? '—'}`;
    case 'lm_growth_pct':
      return `M-1 +${ruleValue?.pct ?? '—'}%`;
    case 'lm_value':
      return t('admin.objectiveRules.summary.lmValue', 'M-1 value (as-is)');
    case 'tier_based':
      return `Per-tier (${Object.keys(ruleValue?.per_tier || {}).length} tiers)`;
    case 'upgrade_pct_of_tier':
      return `${ruleValue?.value ?? '—'}% upgrade per tier`;
    case 'maintain_pct_of_tier':
      return `${ruleValue?.value ?? '—'}% maintain per tier`;
    case 'upgrade_count_per_tier':
      return `Upgrade count (${Object.keys(ruleValue?.per_tier || {}).length} tiers)`;
    case 'maintain_count_per_tier':
      return `Maintain count (${Object.keys(ruleValue?.per_tier || {}).length} tiers)`;
    default:
      return ruleType;
  }
};

const AgencyOverridesSection = ({
  tenantId,
  scopedAgencyId, // own agency for agency_owner/agency_admin; null for tenant/super admin
  canWrite,
  isTenantWideAdmin, // true if tenant_admin or super_admin (can pick agency)
  tenantRules,
}) => {
  const { t } = useTranslation();
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(scopedAgencyId || '');
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);

  // Load agencies under the tenant (for tenant-wide admins)
  useEffect(() => {
    if (!isTenantWideAdmin || !tenantId) return;
    let cancelled = false;
    (async () => {
      const { data, error: dbError } = await supabase
        .from('agencies')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (dbError) {
        setError(dbError.message);
        return;
      }
      setAgencies(data || []);
      if (!selectedAgencyId && data && data.length > 0) {
        setSelectedAgencyId(data[0].id);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTenantWideAdmin, tenantId]);

  // Sync agency_id from props for non-tenant-wide admins
  useEffect(() => {
    if (!isTenantWideAdmin && scopedAgencyId) {
      setSelectedAgencyId(scopedAgencyId);
    }
  }, [isTenantWideAdmin, scopedAgencyId]);

  const load = useCallback(async () => {
    if (!selectedAgencyId) {
      setOverrides([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: dbError } = await supabase
      .from('agency_objective_overrides')
      .select('*')
      .eq('agency_id', selectedAgencyId)
      .order('metric', { ascending: true });
    if (dbError) setError(dbError.message);
    setOverrides(data || []);
    setLoading(false);
  }, [selectedAgencyId]);

  useEffect(() => {
    load();
  }, [load]);

  const tenantRuleByMetric = useMemo(() => {
    const map = {};
    (tenantRules || []).forEach((r) => {
      map[r.metric] = r;
    });
    return map;
  }, [tenantRules]);

  const overriddenMetrics = useMemo(
    () => overrides.map((o) => o.metric),
    [overrides]
  );

  const handleAdd = () => {
    setEditingOverride(null);
    setEditorOpen(true);
  };

  const handleEdit = (override) => {
    setEditingOverride(override);
    setEditorOpen(true);
  };

  const handleRevert = async (override) => {
    if (
      !window.confirm(
        t(
          'admin.objectiveRules.agency.confirmRevert',
          'Revert to tenant rule? The agency-specific override will be removed.'
        )
      )
    )
      return;
    const { error: dbError } = await supabase
      .from('agency_objective_overrides')
      .delete()
      .eq('id', override.id);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    await load();
  };

  const handleSave = useCallback(
    async ({ metric, rule_type, rule_value }) => {
      if (!selectedAgencyId) return;
      const payload = {
        agency_id: selectedAgencyId,
        metric,
        rule_type,
        rule_value,
      };
      const { error: dbError } = await supabase
        .from('agency_objective_overrides')
        .upsert(payload, { onConflict: 'agency_id,metric' });
      if (dbError) throw dbError;
      await load();
    },
    [selectedAgencyId, load]
  );

  return (
    <section className="space-y-3">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-heading text-base md:text-lg font-semibold text-foreground">
            {t('admin.objectiveRules.agency.title', 'Agency overrides')}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t(
              'admin.objectiveRules.agency.subtitle',
              'Override tenant rules for a specific agency. Overrides take priority during target regeneration.'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isTenantWideAdmin && agencies.length > 0 && (
            <select
              value={selectedAgencyId}
              onChange={(e) => setSelectedAgencyId(e.target.value)}
              className="px-3 py-2 rounded-lg bg-card border border-border text-foreground text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
            >
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
          {canWrite && selectedAgencyId && (
            <button
              type="button"
              onClick={handleAdd}
              className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
            >
              <span className="text-base leading-none">+</span>
              {t('admin.objectiveRules.agency.addOverride', 'Add override')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}

      {!selectedAgencyId ? (
        <div className="rounded-2xl bg-card border border-border p-6 text-sm text-muted-foreground text-center">
          {t(
            'admin.objectiveRules.agency.noAgency',
            'No agency selected.'
          )}
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border overflow-hidden">
          {loading ? (
            <div className="p-4 space-y-2">
              {[0, 1].map((i) => (
                <div key={i} className="h-12 rounded skeleton" />
              ))}
            </div>
          ) : overrides.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {t(
                'admin.objectiveRules.agency.empty',
                'No agency overrides yet. The tenant rules apply by default.'
              )}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {overrides.map((o) => {
                const inherited = tenantRuleByMetric[o.metric];
                return (
                  <li
                    key={o.id}
                    className="px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 hover:bg-surface-2/40 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-data text-sm text-foreground font-semibold">
                          {o.metric}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent/10 text-accent border border-accent/20 font-data">
                          {o.rule_type}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning/10 text-warning border border-warning/20">
                          {t('admin.objectiveRules.agency.overrideBadge', 'Override')}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 tabular-nums">
                        {formatRuleSummary(o.rule_type, o.rule_value, t)}
                      </div>
                      {inherited && (
                        <div className="text-[11px] text-muted-foreground mt-1 italic">
                          {t(
                            'admin.objectiveRules.agency.inheritedFrom',
                            'Tenant default: {{type}} → {{summary}}',
                            {
                              type: inherited.rule_type,
                              summary: formatRuleSummary(
                                inherited.rule_type,
                                inherited.rule_value,
                                t
                              ),
                            }
                          )}
                        </div>
                      )}
                    </div>
                    {canWrite && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEdit(o)}
                          className="px-3 py-1.5 rounded-lg bg-card border border-border text-foreground text-xs font-medium hover:bg-muted transition-colors"
                        >
                          {t('admin.objectiveRules.agency.edit', 'Edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRevert(o)}
                          className="px-3 py-1.5 rounded-lg bg-card border border-border text-foreground text-xs font-medium hover:bg-muted transition-colors"
                        >
                          {t(
                            'admin.objectiveRules.agency.revert',
                            'Revert to tenant rule'
                          )}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <RuleEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        initialRule={editingOverride}
        defaultMetrics={DEFAULT_METRICS}
        takenMetrics={overriddenMetrics}
        scope="agency_override"
      />
    </section>
  );
};

export default AgencyOverridesSection;
